import Image from '@polydice/icook-image-helper';
import { captureException } from '@sentry/browser';
import cx from 'classnames';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { isMobile } from '../utility';
import styles from './ProductCover.module.scss';

async function fetchVideoUrl(videoId) {
  try {
    const response = await fetch(
      `https://tv.icook.tw/api/v1/videos/${videoId}`
    );

    if (response.ok) {
      const { data } = await response.json();
      return data.attributes['video-url'];
    }
    throw Error(`Unable to fetch videoUrl with videoId: ${videoId}`);
  } catch (e) {
    e.name = 'GetVideoUrlError';
    throw e;
  }
}

const STATUS = {
  PENDING: 'pending',
  FETCHING: 'fetching',
  DONE: 'done',
  FAILED: 'failed'
};

const PlayerContent = ({ coverImage, videoId }) => {
  const [status, setStatus] = useState(STATUS.PENDING);
  const [player, setPlayer] = useState(null);

  const fetchRealPlayer = async () => {
    setStatus(STATUS.FETCHING);
    try {
      const [videoUrl, Player] = await Promise.all([
        fetchVideoUrl(videoId),
        import('@polydice/react-video-player').then((m) => m.default)
      ]);

      setPlayer(
        <Player
          autoplay={true}
          enforceUnmuted={true} // player default is muted when autoplay enabled.
          docking={!isMobile()}
          sources={[
            {
              src: videoUrl,
              type: 'application/x-mpegURL'
            }
          ]}
        />
      );

      setStatus(STATUS.DONE);
    } catch (e) {
      setStatus(STATUS.FAILED);
      captureException(e);
    }
  };

  switch (status) {
    case STATUS.FETCHING:
      return (
        <div
          className={cx(
            styles.productCoverFakePlayButton,
            styles.productCoverFakePlayButtonFetching
          )}
        >
          <i className="icon-spinner-third-solid" />
        </div>
      );
    case STATUS.DONE:
      return player;

    case STATUS.PENDING:
    case STATUS.FAILED:
    default:
      return (
        <>
          {coverImage}
          <button
            className={styles.productCoverFakePlayButton}
            onClick={fetchRealPlayer}
            aria-label="play-button"
          >
            <i className="icon-play-circle-regular" />
          </button>
        </>
      );
  }
};

PlayerContent.propTypes = {
  coverImage: PropTypes.node.isRequired,
  videoId: PropTypes.number.isRequired
};

export const ProductCover = ({ name, coverUrl, videoId }) => {
  const getCoverImage = () => (
    <Image
      className={cx(styles.productCoverSource, 'img-responsive')}
      loading="eager"
      src={coverUrl}
      srcSet={[872, 1536]}
      sizes=" (max-width: 440px) 872px, 1536px"
      alt={name}
    />
  );

  return (
    <section
      className={cx(
        styles.productCover,
        'ratio-container',
        'ratio-container-16-9'
      )}
    >
      {videoId ? (
        <PlayerContent videoId={videoId} coverImage={getCoverImage()} />
      ) : (
        getCoverImage()
      )}
    </section>
  );
};

ProductCover.propTypes = {
  name: PropTypes.string.isRequired,
  coverUrl: PropTypes.string.isRequired,
  videoId: PropTypes.number
};

export default ProductCover;
